import React, { useEffect, useRef, useState } from "react";
import {
  Sun, Moon, Play, Pause, RotateCcw, ChevronUp, ChevronDown, ChevronLeft,
  ChevronRight, Maximize2, X, Copy, Check, QrCode,
} from "lucide-react";
import { hostUrl, API } from "../lib/api";
import "../studio.css";

export default function TvRoom({ creativeId, onExit, share = false }) {
  const prefersDark = typeof window !== "undefined" && window.matchMedia
    && window.matchMedia("(prefers-color-scheme: dark)").matches;
  const [mode, setMode] = useState(prefersDark ? "dark" : "light");
  const [started, setStarted] = useState(false);
  const [paused, setPaused] = useState(false);
  const [replayKey, setReplayKey] = useState(0);
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const frameRef = useRef(null);
  const screenRef = useRef(null);

  const post = (msg) => {
    try { frameRef.current?.contentWindow?.postMessage({ __cloudtv: true, ...msg }, "*"); } catch (e) { console.debug("tvroom: postMessage to ad frame failed", e); }
  };
  const sendKey = (k) => post({ type: "KEY", key: k });

  useEffect(() => {
    if (!started) return undefined;
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Enter", " "].includes(e.key)) {
        e.preventDefault(); sendKey(e.key);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [started]);

  const togglePause = () => { post({ type: paused ? "PLAY" : "PAUSE" }); setPaused(!paused); };
  const replay = () => { setPaused(false); setReplayKey((k) => k + 1); setStarted(true); };
  const goFullscreen = () => { try { screenRef.current?.requestFullscreen?.(); } catch (e) { console.debug("tvroom: fullscreen request rejected", e); } };

  const demoLink = `${window.location.origin}/demo/${creativeId}`;
  const copyLink = () => {
    navigator.clipboard.writeText(demoLink).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1600); });
  };
  const qrSrc = `${API}/qr?data=${encodeURIComponent(demoLink)}`;

  return (
    <div className={`tvroom ${mode}`} data-testid="tv-room">
      <div className="scene">
        <div className="scene-img" style={{ backgroundImage: `url(/rooms/room-${mode}.png)` }}>
          <div className="tv-screen" ref={screenRef} data-testid="tv-screen">
            {started ? (
              <iframe key={replayKey} ref={frameRef} title="ad" src={hostUrl(creativeId)} allow="autoplay; fullscreen" />
            ) : (
              <button className="tv-start" onClick={() => setStarted(true)} data-testid="tv-start">
                <Play size={20} /> <span>Tap to start</span>
              </button>
            )}
            <div className={`tv-glow ${started ? "on" : ""}`} />
          </div>
        </div>
      </div>

      <div className="tvroom-top">
        <div className="tvroom-brand"><span className="dot" /> CloudTV · Interactive Demo</div>
        <div className="tvroom-top-actions">
          {share && (
            <button className="tvr-chip" onClick={copyLink} data-testid="copy-demo-link">
              {copied ? <Check size={15} /> : <Copy size={15} />}<span>{copied ? "Copied!" : "Copy link"}</span>
            </button>
          )}
          {share && (
            <button className="tvr-chip" onClick={() => setShowQr((s) => !s)} data-testid="toggle-qr">
              <QrCode size={15} /><span>QR</span>
            </button>
          )}
          <button className="tvr-icon" onClick={() => setMode(mode === "dark" ? "light" : "dark")}
            data-testid="room-mode-toggle" title="Toggle room lighting">
            {mode === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button className="tvr-icon" onClick={goFullscreen} data-testid="tv-fullscreen" title="Fullscreen">
            <Maximize2 size={18} />
          </button>
          {onExit && <button className="tvr-icon" onClick={onExit} data-testid="tv-room-exit" title="Exit"><X size={18} /></button>}
        </div>
      </div>

      <div className="tvroom-hud" data-testid="tv-hud">
        <div className="remote" data-testid="tv-remote">
          <div className="dpad">
            <button className="rk up" onClick={() => sendKey("ArrowUp")} data-testid="rk-up"><ChevronUp size={18} /></button>
            <button className="rk left" onClick={() => sendKey("ArrowLeft")} data-testid="rk-left"><ChevronLeft size={18} /></button>
            <button className="rk ok" onClick={() => sendKey("Enter")} data-testid="rk-ok">OK</button>
            <button className="rk right" onClick={() => sendKey("ArrowRight")} data-testid="rk-right"><ChevronRight size={18} /></button>
            <button className="rk down" onClick={() => sendKey("ArrowDown")} data-testid="rk-down"><ChevronDown size={18} /></button>
          </div>
          <div className="remote-row">
            <button className="rbtn" onClick={togglePause} data-testid="rk-pause">
              {paused ? <Play size={15} /> : <Pause size={15} />} {paused ? "Play" : "Pause"}
            </button>
            <button className="rbtn" onClick={replay} data-testid="rk-replay"><RotateCcw size={15} /> Replay</button>
          </div>
        </div>
        <div className="howto">
          <h4>How to interact</h4>
          <ul>
            <li><b>◀ ▶ ▲ ▼</b> move around the ad</li>
            <li><b>OK</b> select, vote or spin</li>
            <li><b>Pause</b> / <b>Replay</b> the video</li>
            <li>Or use keyboard arrows + <b>Enter</b></li>
          </ul>
        </div>
      </div>

      {showQr && (
        <div className="qr-pop" onClick={() => setShowQr(false)} data-testid="qr-pop">
          <div className="qr-card" onClick={(e) => e.stopPropagation()}>
            <img className="qr" src={qrSrc} alt="Scan to open on phone" data-testid="demo-qr" />
            <span>Scan to open this demo on a phone</span>
          </div>
        </div>
      )}
    </div>
  );
}
